import { FORMATS } from "pages/flyer/builder/constants";
import { COMMON_FORMAT_DIMENSIONS_MM } from "pages/flyer/builder/utils/formatDimensions";
import TearawayTemplatePdf from "static/files/TearawayTemplate.pdf";

/**
 * Format metadata for the Flyering (H2H) and Door-to-Door (D2D) design step.
 * Labels, physical dimensions, and download templates are derived from the legacy
 * flyer-builder constants so the values stay in lockstep with the print pipeline.
 *
 * This is the H2H/D2D counterpart to ADM's `getAdmFormatMeta` (postcards). The two
 * stay separate while ADM is mid-QA; the PAD-31539 restructure folds them together.
 */
export interface FlyerFormatMeta {
  value: string;
  /** Human-facing name shown in the disclaimer/dropdown, e.g. "Tearaway", "Door Hanger", "A6". */
  label: string;
  widthMm: number;
  heightMm: number;
  /** Relative price tier ("$"…"$$$$") from the legacy format catalog. */
  price?: string;
  /** Custom-glyph key for FormatIcon (Tearaway / Door Hanger); undefined → parametric rect. */
  iconSizeName?: string;
  /** Download-template asset URL (zip/eps), when one exists for the format. */
  template?: string;
}

const INCH_COUNTRIES = ["US", "CA"];

/**
 * Physical-size label, unit chosen by the account country: US/CA bill/print in
 * inches, every other market in millimetres. Appends the price tier when known.
 */
export const formatSizeLabel = (meta: FlyerFormatMeta, countryCode?: string | null): string => {
  const useInches = !!countryCode && INCH_COUNTRIES.includes(countryCode);
  const dims = useInches
    ? `${(meta.widthMm / 25.4).toFixed(2)} × ${(meta.heightMm / 25.4).toFixed(2)} in`
    : `${meta.widthMm} × ${meta.heightMm} mm`;
  return meta.price ? `${dims} · ${meta.price}` : dims;
};

// Friendly display names. Formats not listed fall back to their raw key (e.g. "A6").
const FRIENDLY_LABEL: Record<string, string> = {
  TEARAWAY: "Tearaway",
  DOOR_HANGER: "Door Hanger",
  A4: "A4",
  A5: "A5",
  A6: "A6",
  DL: "DL",
};

// Formats with a bespoke FormatIcon glyph (others fall back to the parametric rect).
const ICON_SIZE_NAME: Record<string, string> = {
  TEARAWAY: "Tearaway",
  DOOR_HANGER: "Door hanger",
};

// The legacy FORMATS.template field defaults to the Door Hanger EPS for almost every
// format, so it can't be trusted blindly. Only these entries point at an asset that
// actually matches the format; the rest get no download link.
const TEMPLATE_BY_FORMAT: Record<string, string | undefined> = {
  DOOR_HANGER: FORMATS.DOOR_HANGER.template,
  A6: FORMATS.A6.template,
  TEARAWAY: TearawayTemplatePdf,
};

const dimsFor = (format: string): { width: number; height: number } | null =>
  COMMON_FORMAT_DIMENSIONS_MM[format as keyof typeof COMMON_FORMAT_DIMENSIONS_MM] ?? null;

export const getFlyerFormatMeta = (value?: string | null): FlyerFormatMeta | null => {
  if (!value) return null;
  const dims = dimsFor(value);
  if (!dims) return null;
  const formatConfig = FORMATS[value as keyof typeof FORMATS];
  return {
    value,
    label: FRIENDLY_LABEL[value] ?? value,
    widthMm: dims.width,
    heightMm: dims.height,
    price: formatConfig?.price,
    iconSizeName: ICON_SIZE_NAME[value],
    template: TEMPLATE_BY_FORMAT[value],
  };
};

export const getFlyerFormatList = (allowed: string[]): FlyerFormatMeta[] =>
  allowed.map((value) => getFlyerFormatMeta(value)).filter((meta): meta is FlyerFormatMeta => meta !== null);
